/* eslint-disable max-lines */
import { SEPARATOR } from '@hakimo-ui/hakimo/data-access';

import {
  AlarmMedia,
  AlarmStatus,
  Box,
  BoxType,
  LocationAlarm,
  Point,
  Polygon,
} from '@hakimo-ui/hakimo/types';
import { deepEqual } from '@hakimo-ui/hakimo/util';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Props } from './AlarmOverview';
import { TimelineSource } from './event-timeline/types';
dayjs.extend(utc);

export function eventsDomain(sources: TimelineSource[]) {
  const timeStamps = sources.reduce<number[]>((acc, cur) => {
    return [...acc, ...flattenArray(cur.events.map((e) => e.timespan))];
  }, []);

  const min = Math.min(...timeStamps);
  const max = Math.max(...timeStamps);

  return [min, max];
}

export const flattenArray = <T>(arr: T[][]) =>
  arr.reduce<T[]>((acc, val) => acc.concat(val), []);

export function getAlarmTime(data?: AlarmMedia) {
  const markerAlarmTime = data?.videoDetails?.markers.find(
    (m) => m.label === 'Alarm Time'
  );
  return markerAlarmTime?.timestamp ?? 0;
}

function getTagTextFromBoxType(type?: BoxType) {
  switch (type) {
    case BoxType.PERSON:
      return 'Person';
    case BoxType.LOITERING_PERSON:
      return 'Loitering Person';
    case BoxType.PERSON_WITH_WEAPON:
      return 'Weapon';
    case BoxType.VEHICLE:
      return 'Vehicle';
    default:
      return '';
  }
}

function getColorFromBoxType(type?: BoxType): string {
  switch (type) {
    case BoxType.VEHICLE:
      return 'cyan';
    case BoxType.PERSON_WITH_WEAPON:
      return 'red';
    default:
      return 'yellow';
  }
}

export const getVideoPolygons = (boxes: Box[]): Polygon[] => {
  return boxes.map((box) => {
    const [left, top, width, height] = box.coords;
    const points: Point[] = [
      [left, top],
      [left + width, top],
      [left + width, top + height],
      [left, top + height],
    ];
    const polygon: Polygon = {
      color: getColorFromBoxType(box.type),
      pointSize: 'small',
      closed: true,
      points,
      tag: getTagTextFromBoxType(box.type),
    };
    return polygon;
  });
};

export function arePropsEqual(oldProps: Props, newProps: Props) {
  return (
    oldProps.onStatusChange === newProps.onStatusChange &&
    oldProps.dataVersion === newProps.dataVersion &&
    deepEqual(oldProps.alarm, newProps.alarm)
  );
}

export const getAlarmsSearchParams = (
  cameraIds: string[],
  beginTime: number,
  endTime: number,
  locationId: string,
  statuses: AlarmStatus[]
) => {
  const params = new URLSearchParams();
  if (cameraIds && cameraIds.length > 0) {
    const query = cameraIds.join(SEPARATOR);
    params.append('sourceEntityIds', query);
  }
  locationId && params.append('locationIds', locationId);
  beginTime && params.append('dateFrom', String(beginTime));
  endTime && params.append('dateTo', String(endTime));
  if (statuses && statuses.length > 0) {
    const query = statuses.join(SEPARATOR);
    params.append('statuses', query);
  }
  if (beginTime) {
    const date = dayjs.utc(beginTime);
    const partitionKey = date.format('YYYYMM');
    params.append('partitionKey', partitionKey);
  }
  return params.toString();
};

export const getMinorAlarmsSearchParams = (locationAlarm: LocationAlarm) => {
  const params = new URLSearchParams();
  const cameraIds = [
    ...new Set(locationAlarm.rawAlarms.map((alarm) => alarm.sourceEntity.id)),
  ];
  const locationId = String(locationAlarm.location?.id) || '';
  const alarmTimes = locationAlarm.rawAlarms.reduce<number[]>((acc, alarm) => {
    if (alarm.videoStartTime) {
      acc.push(new Date(alarm.videoStartTime).getTime());
    }
    if (alarm.videoEndTime) {
      acc.push(new Date(alarm.videoEndTime).getTime());
    }
    return acc;
  }, []);
  if (alarmTimes.length === 0) {
    return '';
  }
  // adding 1 second ans subtracting 1 second to avoid fetching minor alarms on edges
  const beginTime = Math.min(...alarmTimes) + 1000;
  const endTime = Math.max(...alarmTimes) - 1000;
  let partitionKey = '';
  if (beginTime) {
    const date = dayjs.utc(beginTime);
    partitionKey = date.format('YYYYMM');
  }

  if (cameraIds && cameraIds.length > 0) {
    const query = cameraIds.join(SEPARATOR);
    params.append('sourceEntityIds', query);
  }
  locationId && params.append('locationIds', locationId);
  beginTime && params.append('dateFrom', String(beginTime));
  endTime && params.append('dateTo', String(endTime));
  params.append('statuses', 'Resolved by Hakimo');
  params.append('partitionKey', partitionKey);

  return params.toString();
};
